import { useMutation } from '@apollo/client'
import { message } from 'antd'
import { useDraftedJobIds } from 'hooks/useDraftedJobIds'
import {
  PageContent,
  PageContentWrapper,
  PageSidebar,
  PageWrapper,
} from 'containers/styles'
import { SavedJobsContext } from 'contexts/SavedJobsContext/context'
import { SearchContext } from 'contexts/SearchContext'
import { useWindowSize } from 'contexts/WindowSizeContext'
import { SAVE_JOB, UNSAVE_JOB } from 'graphql/queries/job'
import _isUndefined from 'lodash-es/isUndefined'
import { StringParam, useQueryParams } from 'next-query-params'
import dynamic from 'next/dynamic'
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { SearchPageHeader } from '../../components/SearchResultListComponents'
import { UserProfileContext } from 'contexts/UserProfileContext/context'
import { JobSearchPageListHeader } from './components/SearchPageListHeader/JobSearch'

const JobMoreFilters = dynamic(() =>
  import('components/Modals/MoreFiltersModal/JobMoreFilters').then(
    (res) => res.JobMoreFilters
  )
)
const JobSidebar = dynamic(() =>
  import('components/JobSidebar').then((res) => res.JobSidebar)
)
const BottomSheet = dynamic(() =>
  import('components/BottomSheet').then((res) => res.BottomSheet)
)
const MobileSearchesActionBar = dynamic(() =>
  import('components/MobileSearchesActionBar').then(
    (res) => res.MobileSearchesActionBar
  )
)

const FilterBar = dynamic(() =>
  import('./components/FilterBar').then((res) => res.FilterBar)
)
const SearchPageContent = dynamic(() =>
  import('./components/SearchPageContent').then((res) => res.SearchPageContent)
)
const MobileJobView = dynamic(() =>
  import('./components/MobileJobView').then((res) => res.MobileJobView)
)

const SCROLL_BREAKPOINT = 400

export const SearchPage: React.FC<{ user: any }> = memo(({ user }) => {
  const adminAuthToken = user?.ad_network_admin_authorization_token
  const pageSidebarRef = useRef(null)
  const [query, setQuery] = useQueryParams({
    filter: StringParam,
    term: StringParam,
    filterValue: StringParam,
    jobId: StringParam,
  })
  const { filter, filterValue, jobId } = query
  const { isMobile } = useWindowSize()
  const [saveJobMutation] = useMutation(SAVE_JOB)
  const [unsaveJobMutation] = useMutation(UNSAVE_JOB)
  const [selectedJob, setSelectedJob] = useState(null)
  const [showBottomSheet, setShowBottomSheet] = useState(false)
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [showScrollingHeader, setShowScrollingHeader] = useState(
    pageSidebarRef?.current?.scrollTop > SCROLL_BREAKPOINT
  )
  const { userContentState } = useContext(UserProfileContext)
  const { savedJobs, setSavedJobs } = useContext(SavedJobsContext)
  const [saved, setSaved] = useState(
    selectedJob?.job_reference_clean
      ? savedJobs?.some(
          (j) => j.jobReferenceClean === selectedJob.job_reference_clean
        )
      : false
  )
  const {
    searchResults,
    handleSearch,
    searchState,
    setPreselectedJobFromJobRef,
    loading,
    preSelectedJob,
    filters,
    overview,
    clearSearchState,
    updateSearchProperty,
    addCustomFilter,
    removeCustomFilter,
    isReadyForSearchStateSideEffects,
  } = useContext(SearchContext)

  const { draftedJobIdsCache, removeDraftFromCache, addDraftedJobIdToCache } =
    useDraftedJobIds({
      skip: false,
    })

  const JOB_SEARCH_SORT_OPTIONS = Object.keys(filters)
  JOB_SEARCH_SORT_OPTIONS.unshift('relevancy')

  const MoreFiltersModal = (
    <JobMoreFilters
      isOpen={isFilterModalOpen}
      setIsOpen={setIsFilterModalOpen}
      filters={filters}
      overview={overview}
      clearSearchState={clearSearchState}
      searchState={searchState}
      updateSearchProperty={updateSearchProperty}
      addCustomFilter={addCustomFilter}
      removeCustomFilter={removeCustomFilter}
      isLoading={loading}
    />
  )

  const onToggleMoreFiltersModal = () => setIsFilterModalOpen((val) => !val)

  const isFiltersReady = () => {
    if (filter || filterValue) {
      return false
    }
    return true
  }

  const selectJob = useCallback(
    (selectedJob) => {
      setSelectedJob(selectedJob)
      isMobile && setShowBottomSheet(true)
    },
    [isMobile]
  )

  const saveJob = async (e = null) => {
    e?.stopPropagation()
    message.success('Job saved')
    setSavedJobs((savedJobs) => [
      ...savedJobs,
      {
        jobReferenceClean: selectedJob.job_reference_clean,
        savedAt: new Date().toString(),
      },
    ])
    try {
      const { errors } = (
        await saveJobMutation({
          variables: {
            companyName: selectedJob.company_name,
            logoUrl: selectedJob.company_logo_url || '',
            title: selectedJob.title,
            location: selectedJob.formatted_address,
            expirationDate: selectedJob.posting_deadline,
            jobReferenceClean: selectedJob.job_reference_clean,
          },
        })
      ).data.savedJobSave
      if (errors) {
        message.error('Error saving job')
        setSavedJobs(
          savedJobs.filter(
            (job) => job.jobReferenceClean !== selectedJob.job_reference_clean
          )
        )
        return
      }
    } catch (e) {
      message.error('Error saving job')
      setSavedJobs(
        savedJobs.filter(
          (job) => job.jobReferenceClean !== selectedJob.job_reference_clean
        )
      )
    }
  }

  const unsaveJob = async (e) => {
    e.stopPropagation()
    message.success('Job unsaved')
    setSavedJobs(
      savedJobs.filter(
        (job) => job.jobReferenceClean !== selectedJob.job_reference_clean
      )
    )
    try {
      const { errors } = (
        await unsaveJobMutation({
          variables: {
            jobReferenceClean: selectedJob.job_reference_clean,
          },
        })
      ).data.savedJobUnsave
      if (errors) {
        message.error('Error unsaving job')
        setSavedJobs((savedJobs) => [
          ...savedJobs,
          {
            jobReferenceClean: selectedJob.job_reference_clean,
            savedAt: new Date().toString(),
          },
        ])
        return
      }
    } catch (e) {
      message.error('Error unsaving job')
      setSavedJobs((savedJobs) => [
        ...savedJobs,
        {
          jobReferenceClean: selectedJob.job_reference_clean,
          savedAt: new Date().toString(),
        },
      ])
    }
  }

  const onClose = () => {
    setSelectedJob(null)
    setShowBottomSheet(false)
  }

  const handleScroll = () => {
    const scrollTop = pageSidebarRef?.current?.scrollTop
    if (scrollTop > SCROLL_BREAKPOINT) {
      return setShowScrollingHeader(true)
    }
    setShowScrollingHeader(false)
  }

  useEffect(() => {
    const intervalId = setInterval(handleScroll, 100)
    return () => clearInterval(intervalId)
  }, [])

  useEffect(() => {
    setSaved(
      savedJobs?.some(
        (j) => j.jobReferenceClean === selectedJob?.job_reference_clean
      )
    )
  }, [savedJobs, selectedJob])

  useLayoutEffect(() => {
    if (isMobile) {
      setShowBottomSheet(false)
      setSelectedJob(null)
    }
  }, [isMobile])

  useEffect(() => {
    if (jobId) {
      setPreselectedJobFromJobRef(jobId)
    }
  }, [])

  useEffect(() => {
    if (
      !isFiltersReady() ||
      !isReadyForSearchStateSideEffects ||
      _isUndefined(user)
    )
      return
    handleSearch(userContentState?.rid, adminAuthToken)
    setSelectedJob(null)
  }, [
    isFiltersReady(),
    searchState,
    isReadyForSearchStateSideEffects,
    user,
    userContentState?.rid,
  ])

  // Scrolls to top when job changes
  useEffect(() => {
    if (selectedJob && pageSidebarRef.current) {
      pageSidebarRef.current.scrollTop = 0
    }
  }, [selectedJob, pageSidebarRef])

  useEffect(() => {
    if (selectedJob && jobId !== selectedJob.job_reference_clean) {
      setQuery({ jobId: selectedJob.job_reference_clean }, 'replaceIn')
    }
  }, [selectedJob, jobId])

  return (
    <PageWrapper>
      {!isMobile && (
        <SearchPageHeader>
          <FilterBar />
        </SearchPageHeader>
      )}
      <PageContent marginTop={isMobile ? 0 : 60} noScroll gap="8px" width="50%">
        {isMobile && (
          <MobileSearchesActionBar
            currentPage="Jobs"
            sortTypes={JOB_SEARCH_SORT_OPTIONS}
            sort={searchState.sort}
            setSort={(val) => updateSearchProperty('sort', val)}
            sortDirection={searchState.sortDirection}
            setSortDirection={(val) =>
              updateSearchProperty('sortDirection', val)
            }
            moreFiltersModal={MoreFiltersModal}
            onToggleMoreFiltersModal={onToggleMoreFiltersModal}
          />
        )}
        <JobSearchPageListHeader
          hideBottomBorder={false}
          listLength={searchResults?.length}
        />
        <PageContentWrapper overflowHidden>
          <SearchPageContent
            preSelectedJob={preSelectedJob}
            selectedJob={selectedJob}
            selectJob={selectJob}
            jobSearchResults={isFiltersReady() ? searchResults : []}
            adminAuthToken={adminAuthToken}
            draftedJobIdsCache={draftedJobIdsCache}
            removeDraftFromCache={removeDraftFromCache}
            addDraftedJobIdToCache={addDraftedJobIdToCache}
          />
        </PageContentWrapper>
      </PageContent>
      {!isMobile && (
        <PageSidebar
          ref={pageSidebarRef}
          hideMobile
          fullHeight
          noPadding
          marginTop={60}
        >
          <JobSidebar
            jobRefClean={(selectedJob || preSelectedJob)?.job_reference_clean}
            showScrollingHeader={showScrollingHeader}
            draftedJobIdsCache={draftedJobIdsCache}
            removeDraftFromCache={removeDraftFromCache}
            addDraftedJobIdToCache={addDraftedJobIdToCache}
          />
        </PageSidebar>
      )}
      {isMobile && showBottomSheet && (
        <BottomSheet
          open={showBottomSheet}
          onClose={onClose}
          options={{
            save: saveJob,
            saved,
            unsave: unsaveJob,
            share: selectedJob,
          }}
        >
          <MobileJobView
            loading={loading}
            job={selectedJob}
            onClose={onClose}
            draftedJobIdsCache={draftedJobIdsCache}
            removeDraftFromCache={removeDraftFromCache}
            addDraftedJobIdToCache={addDraftedJobIdToCache}
          />
        </BottomSheet>
      )}
    </PageWrapper>
  )
})

export default SearchPage
